import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature } from 'topojson-client';
import { theme } from '../../styles/theme';

declare global {
  interface Window {
    loggedD3Countries?: boolean;
  }
}

interface D3EqualEarthMapProps {
  darkMode: boolean;
  countryData: Array<{
    countryCode: string;
    countryName: string;
    value: number;
  }>;
  colorScale: (value: number) => string;
  formatValue: (value: number, unit?: string) => string;
  getCurrentMetricUnit: () => string;
  selectedMetric: string;
}

const D3EqualEarthMap: React.FC<D3EqualEarthMapProps> = ({
  darkMode,
  countryData,
  colorScale,
  formatValue,
  getCurrentMetricUnit,
  selectedMetric,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [worldData, setWorldData] = useState<any>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Load world topology data
  useEffect(() => {
    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
      .then(response => response.json())
      .then(data => {
        setWorldData(data);
      })
      .catch(error => console.error('Error loading world data:', error));
  }, []);

  // Draw the map
  useEffect(() => {
    if (!worldData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous content

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Create Equal Earth projection
    const projection = d3.geoEqualEarth()
      .scale(width / 6.5)
      .translate([width / 2, height / 2])
      .precision(0.1);

    const path = d3.geoPath().projection(projection);

    // Convert TopoJSON to GeoJSON
    const countries = feature(worldData, worldData.objects.countries);

    // Create a group for all countries
    const g = svg.append('g');

    // Add zoom behavior (without scroll wheel)
    const zoom = d3.zoom()
      .scaleExtent([0.5, 8])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      })
      .filter((event) => {
        // Disable scroll wheel zoom, only allow other zoom methods
        return event.type !== 'wheel';
      });

    svg.call(zoom as any);

    // Draw countries
    g.selectAll('path')
      .data((countries as any).features)
      .enter()
      .append('path')
      .attr('d', path as any)
      .attr('fill', (d: any) => {
        // Debug first few countries to see available properties
        if (countryData.length > 0 && !window.loggedD3Countries) {
          console.log('D3 country properties:', d.properties);
          console.log('D3 country id:', d.id);
          console.log('Looking for match with countryData:', countryData.slice(0, 3));
          
          // Special check for USA
          if (d.properties?.name === 'United States' || d.properties?.name === 'United States of America') {
            console.log('USA found in D3 data:', d);
            const usaInData = countryData.find(c => c.countryCode === 'USA' || c.countryName?.includes('United States'));
            console.log('USA in countryData:', usaInData);
          }
          window.loggedD3Countries = true;
        }
        
        // Match country data using ISO codes or name
        // The countryData from database has countryCode with ISO-3 codes (USA, CHN, etc.)
        const countryDataItem = countryData.find(item => {
          // Check if this is USA with special handling
          if (item.countryCode === 'USA') {
            if (d.properties?.name === 'United States' || 
                d.properties?.name === 'United States of America' ||
                d.id === '840') { // USA numeric code
              return true;
            }
          }
          
          // For other countries, match by name first (most reliable with world-atlas)
          const countryName = d.properties?.name || d.properties?.NAME;
          if (item.countryName?.toLowerCase() === countryName?.toLowerCase()) {
            return true;
          }
          
          // Some countries have different names in the database vs TopoJSON
          // Add common variations
          if (countryName === 'United Kingdom' && item.countryName === 'United Kingdom of Great Britain and Northern Ireland') return true;
          if (countryName === 'United Kingdom of Great Britain and Northern Ireland' && item.countryName === 'United Kingdom') return true;
          if (countryName === 'Russia' && item.countryName === 'Russian Federation') return true;
          if (countryName === 'Russian Federation' && item.countryName === 'Russia') return true;

          // African countries with abbreviated names in world-atlas
          if (countryName === 'Central African Rep.' && item.countryName === 'Central African Republic') return true;
          if (countryName === 'Dem. Rep. Congo' && (item.countryName === 'Democratic Republic of the Congo' || item.countryName === 'Congo, the Democratic Republic of the' || item.countryName === 'Congo (Democratic Republic of the)')) return true;
          if (countryName === 'Eq. Guinea' && item.countryName === 'Equatorial Guinea') return true;
          if (countryName === 'W. Sahara' && item.countryName === 'Western Sahara') return true;
          if (countryName === 'S. Sudan' && item.countryName === 'South Sudan') return true;
          if (countryName === 'eSwatini' && (item.countryName === 'Eswatini' || item.countryName === 'Swaziland')) return true;
          if (countryName === 'Congo' && item.countryName === 'Republic of the Congo') return true;

          // Other common variations
          if (countryName === 'Tanzania' && (item.countryName === 'United Republic of Tanzania' || item.countryName === 'Tanzania, United Republic of')) return true;
          if (countryName === 'Côte d\'Ivoire' && (item.countryName === 'Ivory Coast' || item.countryName === 'Cote d\'Ivoire')) return true;
          if (countryName === 'Vietnam' && (item.countryName === 'Viet Nam' || item.countryName === 'Vietnam')) return true;
          if (countryName === 'Lao PDR' && (item.countryName === 'Laos' || item.countryName === 'Lao People\'s Democratic Republic')) return true;
          if (countryName === 'Korea' && item.countryName === 'Republic of Korea') return true;
          if (countryName === 'Dem. Rep. Korea' && (item.countryName === 'North Korea' || item.countryName === 'Democratic People\'s Republic of Korea')) return true;
          
          return false;
        });
        
        return countryDataItem ? colorScale(countryDataItem.value) : '#e0e0e0';
      })
      .attr('stroke', darkMode ? '#444' : '#fff')
      .attr('stroke-width', 0.5)
      .on('mouseover', function(event, d: any) {
        // Highlight on hover
        d3.select(this)
          .attr('stroke-width', 2)
          .attr('stroke', theme.colors.primary.main);

        // Find country data using the same matching logic
        const countryName = d.properties?.name || d.properties?.NAME;
        
        const countryDataItem = countryData.find(item => {
          // USA special handling
          if (item.countryCode === 'USA') {
            if (d.properties?.name === 'United States' || 
                d.properties?.name === 'United States of America' ||
                d.id === '840') {
              return true;
            }
          }
          
          // Match by name
          if (item.countryName?.toLowerCase() === countryName?.toLowerCase()) {
            return true;
          }
          
          // Common variations
          if (countryName === 'United Kingdom' && item.countryName === 'United Kingdom of Great Britain and Northern Ireland') return true;
          if (countryName === 'United Kingdom of Great Britain and Northern Ireland' && item.countryName === 'United Kingdom') return true;
          if (countryName === 'Russia' && item.countryName === 'Russian Federation') return true;
          if (countryName === 'Russian Federation' && item.countryName === 'Russia') return true;

          // African countries with abbreviated names in world-atlas
          if (countryName === 'Central African Rep.' && item.countryName === 'Central African Republic') return true;
          if (countryName === 'Dem. Rep. Congo' && (item.countryName === 'Democratic Republic of the Congo' || item.countryName === 'Congo, the Democratic Republic of the' || item.countryName === 'Congo (Democratic Republic of the)')) return true;
          if (countryName === 'Eq. Guinea' && item.countryName === 'Equatorial Guinea') return true;
          if (countryName === 'W. Sahara' && item.countryName === 'Western Sahara') return true;
          if (countryName === 'S. Sudan' && item.countryName === 'South Sudan') return true;
          if (countryName === 'eSwatini' && (item.countryName === 'Eswatini' || item.countryName === 'Swaziland')) return true;
          if (countryName === 'Congo' && item.countryName === 'Republic of the Congo') return true;

          // Other common variations
          if (countryName === 'Tanzania' && (item.countryName === 'United Republic of Tanzania' || item.countryName === 'Tanzania, United Republic of')) return true;
          if (countryName === 'Côte d\'Ivoire' && (item.countryName === 'Ivory Coast' || item.countryName === 'Cote d\'Ivoire')) return true;
          if (countryName === 'Vietnam' && (item.countryName === 'Viet Nam' || item.countryName === 'Vietnam')) return true;
          if (countryName === 'Lao PDR' && (item.countryName === 'Laos' || item.countryName === 'Lao People\'s Democratic Republic')) return true;
          if (countryName === 'Korea' && item.countryName === 'Republic of Korea') return true;
          if (countryName === 'Dem. Rep. Korea' && (item.countryName === 'North Korea' || item.countryName === 'Democratic People\'s Republic of Korea')) return true;

          return false;
        });

        // Show tooltip
        if (tooltipRef.current) {
          const unit = getCurrentMetricUnit();
          const value = countryDataItem ? formatValue(countryDataItem.value, unit) : 'No data';
          
          tooltipRef.current.innerHTML = `
            <div style="padding: 8px; background: ${darkMode ? '#333' : 'white'}; 
                        border: 1px solid ${darkMode ? '#555' : '#ccc'}; 
                        border-radius: 4px; color: ${darkMode ? 'white' : 'black'};">
              <strong>${countryName || 'Unknown'}</strong><br/>
              ${selectedMetric ? `Value: ${value}` : 'No metric selected'}
            </div>
          `;
          tooltipRef.current.style.display = 'block';
          tooltipRef.current.style.left = event.pageX + 10 + 'px';
          tooltipRef.current.style.top = event.pageY - 28 + 'px';
        }
      })
      .on('mouseout', function() {
        // Remove highlight
        d3.select(this)
          .attr('stroke-width', 0.5)
          .attr('stroke', darkMode ? '#444' : '#fff');

        // Hide tooltip
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      });

    // Add graticule (grid lines)
    const graticule = d3.geoGraticule();
    g.append('path')
      .datum(graticule)
      .attr('d', path as any)
      .attr('fill', 'none')
      .attr('stroke', darkMode ? '#333' : '#ddd')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', 0.5);

    // Zoom controls container
    const controls = svg.append('g')
      .attr('transform', `translate(10, 10)`);

    // Zoom in button
    const zoomInButton = controls.append('g')
      .style('cursor', 'pointer')
      .on('click', () => {
        svg.transition()
          .duration(250)
          .call(zoom.scaleBy as any, 1.5);
      });

    zoomInButton.append('rect')
      .attr('width', 30)
      .attr('height', 30)
      .attr('rx', 2)
      .attr('fill', darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.9)')
      .attr('stroke', darkMode ? theme.colors.border.dark : theme.colors.border.light)
      .attr('stroke-width', 2);

    zoomInButton.append('text')
      .attr('x', 15)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('font-size', '20px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text('+');

    // Zoom out button
    const zoomOutButton = controls.append('g')
      .attr('transform', `translate(0, 32)`)
      .style('cursor', 'pointer')
      .on('click', () => {
        svg.transition()
          .duration(250)
          .call(zoom.scaleBy as any, 0.67);
      });

    zoomOutButton.append('rect')
      .attr('width', 30)
      .attr('height', 30)
      .attr('rx', 2)
      .attr('fill', darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.9)')
      .attr('stroke', darkMode ? theme.colors.border.dark : theme.colors.border.light)
      .attr('stroke-width', 2);

    zoomOutButton.append('text')
      .attr('x', 15)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('font-size', '20px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text('−');

    // Reset button (optional - you can remove this if not needed)
    const resetButton = controls.append('g')
      .attr('transform', `translate(40, 10)`)
      .style('cursor', 'pointer')
      .on('click', () => {
        svg.transition()
          .duration(750)
          .call(zoom.transform as any, d3.zoomIdentity);
      });

    resetButton.append('rect')
      .attr('width', 80)
      .attr('height', 30)
      .attr('rx', 4)
      .attr('fill', darkMode ? theme.colors.background.dark : theme.colors.background.default)
      .attr('stroke', darkMode ? theme.colors.border.dark : theme.colors.border.light);

    resetButton.append('text')
      .attr('x', 40)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary)
      .text('Reset View');

  }, [worldData, countryData, colorScale, darkMode, selectedMetric, formatValue, getCurrentMetricUnit]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0',
        }}
      />
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          display: 'none',
          pointerEvents: 'none',
          zIndex: 1000,
          fontSize: '12px',
          fontFamily: 'Arial, sans-serif',
        }}
      />
    </div>
  );
};

export default D3EqualEarthMap;